// ============================================================
// src/domain/rules.ts — TARCZA MARŻY
// Zero UI. Czysta logika. Testowalny offline.
// ============================================================

import { Estimate, BusinessRule, SpatialData } from './types';
import {
  MIN_RBG_2026,
  HYDROIZOLACJA_PLN_M2,
  GRUNT_PLN_M2,
  AKRYL_PLN_MB,
} from '../core/zasady/constants';
import { roundMoney } from './money';

export function validateBusinessRules(
  estimate: Estimate,
  selectedItemIds: string[] = []
): BusinessRule[] {
  const rules: BusinessRule[] = [];
  const s = estimate.spatial;
  const costs = estimate.costs;

  // ── 1. KIO: stawka za r-g poniżej minimum ───────────────
  // Szacunkowe r-g: 1m² gładzi ≈ 0.45h, malowanie ≈ 0.25h
  const estHours = s.flatSurfaceM2 * 0.35;
  if (estHours > 0) {
    const effectiveHourlyRate = costs.labor / estHours;
    if (effectiveHourlyRate < MIN_RBG_2026) {
      rules.push({
        type: 'KIO_WARNING',
        active: true,
        message: `Stawka ${effectiveHourlyRate.toFixed(2)} zł/h < KIO ${String(MIN_RBG_2026)} zł/h — ryzyko odrzucenia oferty. Sprawdź stawkę przed wysłaniem.`,
      });
    }
  }

  // ── 2. GRUNT brak przy malowaniu/gładzi ─────────────────
  const hasGladz = selectedItemIds.includes('gladz') 
    || selectedItemIds.includes('malowanie');
  const hasGrunt = selectedItemIds.includes('grunt');
  
  if (hasGladz && !hasGrunt && s.flatSurfaceM2 > 0) {
    rules.push({
      type: 'PRIMER_MISSING',
      active: true,
      lossAmount: roundMoney(s.flatSurfaceM2 * GRUNT_PLN_M2 * 1.5),
      // *1.5 = koszt poprawki (materiał + robocizna) gdyby się odparzyła
    });
  }

  // ── 3. HYDROIZOLACJA brak w strefie mokrej ───────────────
  if (s.wetZone && !selectedItemIds.includes('hydroizolacja')) {
    rules.push({
      type: 'HYDRO_MISSING',
      active: true,
      lossAmount: roundMoney((s.floorAreaM2 + (s.edgesMB * 0.3))
        * HYDROIZOLACJA_PLN_M2),
    });
  }

  // ── 4. AKRYLOWANIE narożników ────────────────────────────
  if (s.edgesMB > 4 && !selectedItemIds.includes('akryl')) {
    rules.push({
      type: 'ACRYLIC_MISSING',
      active: true,
      lossAmount: roundMoney(s.edgesMB * AKRYL_PLN_MB),
    });
  }

  // ── 5. WILGOĆ — ryzyko dodatkowych prac ─────────────────
  if (s.moistureDetected) {
    rules.push({
      type: 'MOISTURE_RISK',
      active: true,
      lossAmount: roundMoney(s.flatSurfaceM2 * 12),
      // ~12 zł/m² osuszanie + preparat
    });
  }

  return rules;
}

// Walidacja na podstawie wyników AI Vision
export function validateFromAIVision(
  spatial: SpatialData,
  detectedGaps: string[],
  surfaceCondition: string,
  otworyStandardoweSzt: number,
  otworyDuzeM2: number,
  glifyMb: number,
): BusinessRule[] {
  const rules: BusinessRule[] = [];

  // 1. Wykryto brak gruntu przez AI
  if (detectedGaps.includes('missing_grunt') || surfaceCondition === 'goły_tynk') {
    rules.push({
      type: "PRIMER_MISSING",
      active: true,
      lossAmount: roundMoney(spatial.flatSurfaceM2 * GRUNT_PLN_M2 * 1.5),
    });
  }

  // 2. Wykryto wilgoć przez AI
  if (detectedGaps.includes('moisture_detected') || surfaceCondition === 'wilgotny') {
    rules.push({
      type: "MOISTURE_RISK",
      active: true,
      lossAmount: roundMoney(spatial.flatSurfaceM2 * 12),
    });
  }

  // 3. Brak hydroizolacji w łazience
  if (detectedGaps.includes('missing_hydro')) {
    rules.push({
      type: "HYDRO_MISSING",
      active: true,
      lossAmount: roundMoney((spatial.floorAreaM2 + (spatial.edgesMB * 0.3)) * HYDROIZOLACJA_PLN_M2),
    });
  }

  // 4. Glify (narożniki) - dodatkowy koszt robocizny
  if (glifyMb > 0) {
    rules.push({
      type: "ACRYLIC_MISSING",
      active: true,
      lossAmount: roundMoney(glifyMb * AKRYL_PLN_MB),
    });
  }

  return rules;
}

// ── Helpers eksportowane dla UI ──────────────────────────────

export function calculateTotalLoss(rules: BusinessRule[]): number {
  return rules.reduce((sum, rule) => {
    if (rule.active && 'lossAmount' in rule) {
      return sum + rule.lossAmount;
    }
    return sum;
  }, 0);
}

export function getTotalLoss(rules: BusinessRule[]): number {
  return rules
    .filter(r => r.active)
    .reduce((sum, r) => {
      if (r.type === 'KIO_WARNING') return sum;
      return sum + (('lossAmount' in r) ? r.lossAmount : 0);
    }, 0);
}

export function formatLossMessage(rule: BusinessRule): string {
  switch (rule.type) {
    case "PRIMER_MISSING":
      return `🔴 Sprawdź gruntowanie. Pozycja do doliczenia: ${rule.lossAmount.toFixed(0)} PLN (ryzyko odprysków).`;
    case "HYDRO_MISSING":
      return `🔴 Sprawdź hydroizolację. Pozycja do doliczenia: ${rule.lossAmount.toFixed(0)} PLN. Wilgoć może uszkodzić pracę.`;
    case "MOISTURE_RISK":
      return `⚠️ Wilgoć do sprawdzenia. Wynajem osuszacza: ${rule.lossAmount.toFixed(0)} PLN. Sprawdź, czy uwzględnione w wycenie.`;
    case "ACRYLIC_MISSING":
      return `⚠️ Sprawdź akrylowanie/glify. Pozycja do doliczenia: ${rule.lossAmount.toFixed(0)} PLN.`;
    case "KIO_WARNING":
      return rule.message;
    default:
      return "Nieznana reguła";
  }
}

export function getRuleLabel(rule: BusinessRule): string {
  switch (rule.type) {
    case 'KIO_WARNING':     return '🚨 STAWKA KIO — RYZYKO ODRZUCENIA';
    case 'PRIMER_MISSING':  return '🔴 GRUNTOWANIE — pozycja do sprawdzenia';
    case 'HYDRO_MISSING':   return '🔴 BRAK HYDROIZOLACJI — strefa mokra';
    case 'ACRYLIC_MISSING': return '⚠️ BRAK AKRYLOWANIA NAROŻNIKÓW';
    case 'MOISTURE_RISK':   return '⚠️ WYKRYTO WILGOĆ — ryzyko poprawek';
    default: return '⚠️ Alert technologiczny';
  }
}
